import React, { useState, Fragment } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTenant } from "../../contexts/TenantContext";
import { ThemeToggle } from "../ThemeToggle";
import { Menu, Transition } from "@headlessui/react";
import {
  HomeIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  Squares2X2Icon,
  TrashIcon,
  KeyIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import ChangePasswordModal from "./ChangePasswordModal";

export default function Navigation() {
  const { state, logout } = useAuth();
  const { customization } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Menu items for dropdown
  const menuItems = [];

  // Add standard items for non-super admins
  if (state.user?.role !== "super_admin") {
    menuItems.push(
      { name: "Máquinas", href: "/machines", icon: ComputerDesktopIcon },
      { name: "Alertas", href: "/alerts", icon: ExclamationTriangleIcon },
      { name: "Relatórios", href: "/reports", icon: ChartBarIcon },
      { name: "Lixeira", href: "/machines/trash", icon: TrashIcon }
    );
  }

  // Add super admin menu item
  if (state.user?.role === "super_admin") {
    menuItems.push({
      name: "Empresas",
      href: "/super-admin/tenants",
      icon: BuildingOfficeIcon,
    });
  }

  // Add tenant admin menu item
  if (state.user?.role === "tenant_admin") {
    menuItems.push({
      name: "Configurações",
      href: "/settings",
      icon: Squares2X2Icon,
    });
  }

  const isActive = (href: string) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  return (
    <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                {customization.logoUrl ? (
                  <img
                    src={customization.logoUrl}
                    alt="Logo"
                    className="h-10 w-auto"
                  />
                ) : (
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    Hub Machines: Repositório de Maquinas
                  </span>
                )}
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8 sm:items-center">
              {/* Dashboard - Always visible */}
              <Link
                to="/"
                className={`${
                  isActive("/")
                    ? "border-blue-500 text-gray-900 dark:text-white"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16`}
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Dashboard
              </Link>

              {/* Dropdown Menu */}
              <Menu as="div" className="relative inline-block text-left">
                <div>
                  <Menu.Button className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none">
                    <Squares2X2Icon className="w-4 h-4 mr-2" />
                    Menu
                    <ChevronDownIcon className="w-4 h-4 ml-2" />
                  </Menu.Button>
                </div>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 z-10 mt-2 w-56 origin-top-left rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      {menuItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Menu.Item key={item.name}>
                            {({ active }) => (
                              <Link
                                to={item.href}
                                className={`${
                                  active ? "bg-gray-100 dark:bg-gray-700" : ""
                                } ${
                                  isActive(item.href)
                                    ? "text-blue-600 dark:text-blue-400"
                                    : "text-gray-700 dark:text-gray-300"
                                } group flex items-center px-4 py-2 text-sm`}
                              >
                                <Icon
                                  className="mr-3 h-5 w-5"
                                  aria-hidden="true"
                                />
                                {item.name}
                              </Link>
                            )}
                          </Menu.Item>
                        );
                      })}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>

          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <UserIcon className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {state.user?.email}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {state.user?.role === "super_admin"
                    ? "Super Admin"
                    : state.user?.role === "tenant_admin"
                    ? "Admin"
                    : "Usuário"}
                </span>
              </div>
              <ThemeToggle />
              <button
                onClick={() => setIsChangePasswordModalOpen(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                title="Alterar senha"
              >
                <KeyIcon className="w-4 h-4" />
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                Sair
              </button>
            </div>
          </div>

          <div className="sm:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="sm:hidden bg-white dark:bg-gray-800">
          <div className="pt-2 pb-3 space-y-1">
            {/* Dashboard */}
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`${
                isActive("/")
                  ? "bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                  : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
            >
              <div className="flex items-center">
                <HomeIcon className="w-5 h-5 mr-3" />
                Dashboard
              </div>
            </Link>

            {/* Menu Items */}
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`${
                    isActive(item.href)
                      ? "bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                      : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                >
                  <div className="flex items-center">
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                <UserIcon className="w-8 h-8 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800 dark:text-gray-200">
                  {state.user?.email}
                </div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {state.user?.role === "super_admin"
                    ? "Super Admin"
                    : state.user?.role === "tenant_admin"
                    ? "Admin"
                    : "Usuário"}
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <button
                onClick={() => {
                  setIsChangePasswordModalOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 dark:hover:text-gray-200 w-full text-left"
              >
                <div className="flex items-center">
                  <KeyIcon className="w-5 h-5 mr-3" />
                  Alterar Senha
                </div>
              </button>
              <button
                onClick={handleLogout}
                className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 dark:hover:text-gray-200 w-full text-left"
              >
                <div className="flex items-center">
                  <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                  Sair
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </nav>
  );
}
